import { useTaskList } from "@/models/richContent/taskListLifecycle";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import React from "react";
import { useTranslation } from "react-i18next";

import * as People from "@/models/people";

import { Feed, useFeedItemsQuery } from "@/features/Feed";
import { PageModule } from "@/routes/types";
import { assertPresentOr404 } from "@/utils/assertions";
import { ProfilePage } from "turboui";

import { loader, useLoadedData } from "./loader";

import { compareIds, usePaths } from "@/routes/paths";
import { convertToWorkMapItems } from "../../models/workMap";
import { useMe } from "@/contexts/CurrentCompanyContext";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";

export default { name: "ProfilePage", loader, Page } as PageModule;

function Page() {
  const { t } = useTranslation();
  const paths = usePaths();
  const me = useMe();
  const { mentionedPersonLookup } = useRichEditorHandlers();
  const formattedTimePreferences = useFormattedTimePreferences();
  const { person, workMap, reviewerWorkMap, taskOrdering } = useLoadedData();
  const taskOrderingMutation = People.useUpdateTaskOrdering();
  const isOwnProfile = compareIds(me?.id, person.id);
  const taskList = useTaskList({
    resourceType: "person",
    resourceId: person.id,
    field: "description",
    canEdit: compareIds(me?.id, person.id) && canEditProfile(person),
  });

  const parsedPerson = People.parsePersonForTurboUi(paths, person);
  const viewer = People.parsePersonForTurboUi(paths, me) || null;
  const manager = People.parsePersonForTurboUi(paths, person.manager);

  assertPresentOr404(parsedPerson);

  const props = {
    title: [person.fullName, t("Profile")],

    viewer,
    person: parsedPerson,
    peers: People.parsePeopleForTurboUi(paths, People.sortByName(person.peers || [])),
    reports: People.parsePeopleForTurboUi(paths, People.sortByName(person.reports || [])),
    manager,

    workMap: convertToWorkMapItems(paths, workMap),
    reviewerWorkMap: convertToWorkMapItems(paths, reviewerWorkMap),
    taskOrdering: isOwnProfile ? taskOrdering : [],
    onTaskOrderingChange: taskOrderingMutation.save,
    taskOrderingSaving: taskOrderingMutation.isPending,

    canEditProfile: canEditProfile(person),
    editProfilePath: paths.profileEditPath(person.id),

    activityFeed: <ActivityFeed personId={person.id} />,
    aboutMe: person.description,
    mentionedPersonLookup,
    taskList,
    formattedTimePreferences,
  };

  return <ProfilePage {...props} />;
}

function ActivityFeed({ personId }: { personId: string }) {
  const { t } = useTranslation();
  const { data, loading, error, pagination } = useFeedItemsQuery("person", personId);

  if (loading) return <div>{t("Loading...")}</div>;
  if (error) return <div>{t("Error")}</div>;

  return <Feed pagination={pagination} items={data?.activities || []} testId="profile-feed" page="profile" />;
}

function canEditProfile(person: People.Person) {
  return person.permissions?.canEditProfile ?? false;
}
